import { Card, IconButton, Icon, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Typography, Grid, TextField, Autocomplete } from "@mui/material";
import { useState, useEffect, useRef } from "react";
import DataTable from "../../../../Components/Table/DataTable";
import { Button as MuiButton } from "@mui/material";
import WDBox from "../../../../MaterialComponents/WDBox";
import moment from "moment";
import AppService from "../../../../Configurations/Appservice";
import TableLoader from "../../../../Components/AppLoader/TableLoader";
import { useNavigate } from "react-router-dom";
import { updateScreenTitle } from "../../../../LayoutContainers/AppLayout/redux/AppSlice";
import { useDispatch } from "react-redux";
import { Alert, Snackbar, AlertTitle, CircularProgress } from "@mui/material";
import { FORMAT_DATE } from "../../../../Configurations/JsUtil";

const tableInitialData = {
  columns: [
    { 
      Header: "service name", 
      accessor: "name",
      Cell: ({ cell: { value }, row: { original } }) => (
        <div title={value} style={{ cursor: 'default' }}>
          {value}
        </div>
      ) 
    },
    { 
      Header: "description", 
      accessor: "description",
      Cell: ({ cell: { value } }) => (
        <div title={value} style={{ cursor: 'default', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {value}
        </div>
      )
    },
    { Header: "groupName", accessor: "groupName" },
    { Header: "document type", accessor: "organizationDocumentTypeName" },
    { Header: "govt.charge", accessor: "govtCharge" },
    { Header: "service charge", accessor: "serviceCharge" },
    { Header: "tax on service charge", accessor: "taxOnServiceCharge" },
    { Header: "created Date", accessor: "createDateTime" },
    { Header: "edit", accessor: "edit" },
    // Adding export action column
    { Header: "export", accessor: "export" },
  ],

  rows: [],
};

// Enhanced filters with dynamic service group options
const getServiceGroupOptions = (rows) => {
  // Extract unique service groups from table rows
  const uniqueGroups = [...new Set(rows.map(row => row.groupName).filter(Boolean))];
  return uniqueGroups.map(group => ({ label: group, value: group }));
};

const tableFilters = [
  {
    accessor: "groupName",
    type: "LIST",
    labelName: "Service Group",
    // This will be populated dynamically when we have data
    options: [],
  },
  {
    accessor: "name",
    type: "TEXT",
    labelName: "Search",
  },
];

const Services = () => {
  const [tableData, setTableData] = useState(tableInitialData);
  const [isLoading, loading] = useState(true);
  const [filters, setFilters] = useState(tableFilters);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importInProgress, setImportInProgress] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  
  const fileInputRef = useRef(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(updateScreenTitle("Services"));
    return () => dispatch(updateScreenTitle(""));
  }, []);

  const _generateTableRows = (response) => {
    const rows = response.map((item) => ({
      ...item,
      createDateTime: FORMAT_DATE(item.createDateTime),
      edit: (
        <IconButton size="small" onClick={() => _navigateToEditScreen(item.id)}>
          <Icon>edit</Icon>
        </IconButton>
      ),
      export: (
        <IconButton size="small" color="primary" onClick={_exportServicesToCSV}>
          <Icon>file_download</Icon>
        </IconButton>
      ),
    }));

    // Update tableFilters with dynamic service group options
    const serviceGroups = getServiceGroupOptions(rows);
    const updatedFilters = [...tableFilters];
    const groupFilterIndex = updatedFilters.findIndex(filter => filter.accessor === "groupName");
    if (groupFilterIndex !== -1) {
      updatedFilters[groupFilterIndex] = {
        ...updatedFilters[groupFilterIndex],
        options: serviceGroups,
      };
    }
    
    let tableDataDuplicate = { ...tableData };
    tableDataDuplicate.rows = rows;
    setTableData(tableDataDuplicate);
    
    // Update filters state with dynamic service group options
    setFilters(updatedFilters);
  };

  useEffect(() => {
    AppService.get("v1/service")
      .then(_generateTableRows)
      .catch((error) => {})
      .finally(() => {
        loading(false);
      });
  }, []);

  const _navigateToCreationScreen = () => {
    navigate("/ADM-Service/adm-new-service");
  };

  const _navigateToEditScreen = (serviceId) => {
    navigate("/ADM-Service/adm-new-service", { state: { serviceId } });
  };
  
  // Open import dialog and reset state
  const _openImportDialog = () => {
    setImportResult(null);
    setImportDialogOpen(true);
  };

  // Close import dialog
  const _closeImportDialog = () => {
    setImportDialogOpen(false);
    setImportResult(null);
  };

  // Handle file selection
  const _handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  // Handle file upload and import
  const _handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Reset the file input value to allow selecting the same file again
    event.target.value = '';

    // Show loading indicator
    setImportInProgress(true);

    // Create form data
    const formData = new FormData();
    formData.append('file', file);

    // Call the import API
    AppService.post('v1/service/import/csv', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
      .then((response) => {
        setImportResult(response);

        // Show success message
        if (response.success) {
          setSnackbarSeverity('success');
          setSnackbarMessage(`Import successful: ${response.createdCount} services created, ${response.updatedCount} updated`);
        } else {
          setSnackbarSeverity('error');
          setSnackbarMessage(`Import failed: ${response.message || 'Unknown error'}`);
        }
        setSnackbarOpen(true);

        // Refresh the service list if import was successful
        if (response.success) {
          AppService.get('v1/service')
            .then(_generateTableRows)
            .catch((error) => {})
            .finally(() => {});
        }
      })
      .catch((error) => {
        console.error('Error importing services:', error);
        setSnackbarSeverity('error');
        setSnackbarMessage(`Import failed: ${error.response?.data?.message || error.message || 'Unknown error'}`);
        setSnackbarOpen(true);
      })
      .finally(() => {
        setImportInProgress(false);
      });
  };

  // Custom toolbar component with export and import buttons
  const CustomToolbar = () => {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
        {/* Hidden file input for CSV upload */}
        <input
          type="file"
          accept=".csv"
          hidden
          ref={fileInputRef}
          onChange={_handleFileUpload}
        />

        {/* Import CSV button */}
        <MuiButton
          variant="contained"
          color="primary"
          startIcon={<Icon>file_upload</Icon>}
          onClick={_openImportDialog}
          disabled={isLoading}
          size="small"
          sx={{ mr: 1 }}
        >
          Import CSV
        </MuiButton>

        {/* Export CSV button */}
        <MuiButton
          variant="contained"
          color="primary"
          startIcon={<Icon>file_download</Icon>}
          onClick={_exportServicesToCSV}
          disabled={isLoading}
          size="small"
          sx={{ mr: 1 }}
        >
          Export CSV
        </MuiButton>
      </Box>
    );
  };

  const _exportServicesToCSV = () => {
    // Show loading indicator
    loading(true);
    
    // Create a direct download link for the CSV file
    const timestamp = moment().format("YYYYMMDD_HHmmss");
    const filename = `services_export_${timestamp}.csv`;
    
    // Get the API base URL and token
    const baseUrl = process.env.REACT_APP_API_URL;
    
    // Get the JWT token using the correct approach from auth.js
    // First try jwt_token, then fall back to authInfo if needed
    let token = localStorage.getItem('jwt_token');
    if (!token) {
      try {
        const authInfo = localStorage.getItem('authInfo');
        if (authInfo) {
          const authInfoObj = JSON.parse(authInfo);
          if (authInfoObj.token) {
            token = authInfoObj.token;
          }
        }
      } catch (error) {
        console.error('Error retrieving token from authInfo:', error);
      }
    }
    
    if (!token) {
      console.error('No authentication token found for export');
    }
    
    // Create the download URL
    const url = `${baseUrl}/v1/service/export/csv`;
    
    // Create an anchor element for the download
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    
    // Add authentication header by creating a fetch request
    fetch(url, {
      method: "GET",
      headers: {
        "Authorization": `Bearer ${token}`
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`Export failed: ${response.status} ${response.statusText}`);
      }
      return response.blob();
    })
    .then(blob => {
      // Create a URL for the blob
      const url = window.URL.createObjectURL(blob);
      
      // Set up the download link
      link.href = url;
      link.download = `services_export_${moment().format("YYYYMMDD_HHmmss")}.csv`;
      
      // Append to body, click and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Release the blob URL
      window.URL.revokeObjectURL(url);
    })
    .catch(error => {
      console.error("Error exporting services:", error);
      alert("Failed to export services. Please try again.");
    })
    .finally(() => {
      loading(false);
    });
  };

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Keep the original data separate from filtered data
  const [originalData, setOriginalData] = useState({ columns: tableData.columns, rows: [] });
  const [filterValues, setFilterValues] = useState({
    groupName: null,
    name: ""
  });

  // Update originalData when data is loaded
  useEffect(() => {
    if (tableData.rows && tableData.rows.length > 0) {
      setOriginalData({ columns: tableData.columns, rows: [...tableData.rows] });
    }
  }, [tableData.rows.length]);

  // Apply filters whenever filter values change
  useEffect(() => {
    if (originalData.rows.length === 0) return;
    
    // When clearing filters, ensure we reset to original data
    if (!filterValues.groupName && !filterValues.name) {
      console.log("Clearing filters - resetting to original data");
      setTableData({
        columns: tableData.columns,
        rows: [...originalData.rows]
      });
      return;
    }
    
    let filteredRows = [...originalData.rows];
    
    // Apply group filter if set
    if (filterValues.groupName) {
      filteredRows = filteredRows.filter(row => 
        row.groupName && row.groupName.toLowerCase() === filterValues.groupName.toLowerCase()
      );
    }
    
    // Apply name search if set
    if (filterValues.name) {
      filteredRows = filteredRows.filter(row => 
        row.name && row.name.toLowerCase().includes(filterValues.name.toLowerCase())
      );
    }
    
    // Update the table with filtered data
    setTableData({
      columns: tableData.columns,
      rows: filteredRows
    });
  }, [filterValues, originalData.rows.length]);

  // Filter bar component to display filters above the table
  const FilterBar = () => {
    // Use refs to access the filter components directly
    const groupFilterRef = useRef(null);
    const nameFilterRef = useRef(null);

    const handleGroupFilterChange = (value) => {
      // When value is null, it means the X was clicked
      if (value === null) {
        // Reset the group filter
        setFilterValues(prev => ({ ...prev, groupName: null }));
        
        // If no other filters are active, reset to original data
        if (!filterValues.name) {
          reloadServices();
        }
      } else {
        setFilterValues(prev => ({ ...prev, groupName: value }));
      }
    };
    
    const handleNameFilterChange = (value) => {
      setFilterValues(prev => ({ ...prev, name: value }));
    };
    
    // Helper function to reload services
    const reloadServices = () => {
      AppService.get("v1/service")
        .then((response) => {
          if (response.data && response.data.length) {
            const rows = response.data.map((item) => {
              return {
                id: item.id,
                name: item.name,
                description: item.description,
                groupName: item.groupName,
                govtCharge: item.govtCharge,
                serviceCharge: item.serviceCharge,
                taxOnServiceCharge: item.taxOnServiceCharge,
                createdDate: item.createdDate ? new Date(item.createdDate).toLocaleDateString() : "",
              };
            });
            
            setTableData({
              ...tableData,
              rows: rows,
            });
            
            setOriginalData({
              columns: tableData.columns,
              rows: [...rows]
            });
          }
        })
        .catch((error) => {
          console.error("Error loading services:", error);
        });
    };
    
    const clearFilters = () => {
      // First set the filter values to null/empty
      setFilterValues({
        groupName: null,
        name: ""
      });
      
      // Force reload all services to ensure complete reset
      reloadServices();
    };
    
    return (
      <Box sx={{ p: 2, backgroundColor: "#f5f5f5", borderRadius: "4px", mb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="subtitle1">Filters</Typography>
          <Button 
            variant="outlined" 
            size="small" 
            onClick={clearFilters}
            startIcon={<Icon>clear</Icon>}
          >
            Clear Filters
          </Button>
        </Box>
        <Grid container spacing={2}>
          {/* Service Group Filter */}
          <Grid item xs={12} md={4}>
            <Autocomplete
              id="service-group-filter"
              options={getServiceGroupOptions(originalData.rows)}
              getOptionLabel={(option) => option.label || ""}
              value={filterValues.groupName ? {label: filterValues.groupName, value: filterValues.groupName} : null}
              renderInput={(params) => (
                <TextField {...params} label="Service Group" variant="outlined" size="small" />
              )}
              onChange={(event, value) => handleGroupFilterChange(value ? value.value : null)}
              key={`group-filter-${originalData.rows.length}-${filterValues.groupName || 'null'}`}
              disableClearable={false}
              clearOnEscape={true}
            />
          </Grid>
          
          {/* Search Filter */}
          <Grid item xs={12} md={4}>
            <TextField
              id="search-filter"
              label="Search by Name"
              variant="outlined"
              size="small"
              fullWidth
              value={filterValues.name}
              onChange={(e) => handleNameFilterChange(e.target.value)}
              InputProps={{
                endAdornment: filterValues.name ? (
                  <IconButton
                    size="small"
                    onClick={() => {
                      handleNameFilterChange("");
                      // If no other filters are active, reload all services
                      if (!filterValues.groupName) {
                        reloadServices();
                      }
                    }}
                    edge="end"
                  >
                    <Icon>clear</Icon>
                  </IconButton>
                ) : null,
              }}
            />
          </Grid>
        </Grid>
      </Box>
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Custom toolbar with export and import buttons */}
      <CustomToolbar />
      
      {/* Add the filter bar */}
      <FilterBar />
      
      {
        <DataTable
          table={tableData}
          showAddButton={true}
          addButtonOnClick={_navigateToCreationScreen}
          isLoading={isLoading}
          filters={filters}
        />
      }

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onClose={_closeImportDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Import Services from CSV</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Upload a CSV file with service data. The CSV must have the following headers:
            <Box component="ul" sx={{ mt: 1, ml: 2 }}>
              <li>name (required)</li>
              <li>group_name (required)</li>
              <li>description</li>
              <li>govt_charge</li>
              <li>service_charge</li>
              <li>tax_on_service_charge</li>
            </Box>
          </DialogContentText>

          {importInProgress ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : importResult ? (
            <Box sx={{ mt: 2 }}>
              <Alert severity={importResult.success ? "success" : "error"} sx={{ mb: 2 }}>
                <AlertTitle>{importResult.success ? "Import Successful" : "Import Failed"}</AlertTitle>
                {importResult.success ? (
                  <>
                    Successfully imported services:<br />
                    <strong>Created: {importResult.createdCount}</strong><br />
                    <strong>Updated: {importResult.updatedCount}</strong>
                  </>
                ) : (
                  importResult.message || "Error importing services"
                )}
              </Alert>

              {importResult.errors && importResult.errors.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <DialogContentText color="error" variant="subtitle1">
                    The following errors were found:
                  </DialogContentText>
                  <Box component="ul" sx={{ mt: 1, ml: 2 }}>
                    {importResult.errors.map((error, index) => (
                      <Box component="li" key={index} sx={{ mb: 1 }}>
                        {error.line ? `Line ${error.line}: ` : ""}{error.message}
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          {!importInProgress && (
            <>
              <Button onClick={_closeImportDialog}>Cancel</Button>
              <Button
                onClick={_handleFileSelect}
                variant="contained"
                color="primary"
                startIcon={<Icon>file_upload</Icon>}
              >
                Select CSV File
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Services;
